import { useQuery } from '@tanstack/react-query'
import { useApi } from './use-api'

type LinkedInStatus = 'not_connected' | 'connected' | 'reconnect_needed'

type StatusResponse = {
  readonly success: boolean
  readonly status: LinkedInStatus
  readonly account: {
    readonly name: string
    readonly connected_at: string | null
    readonly unipile_account_id: string
  } | null
}

export const linkedInStatusKey = ['linkedin', 'status'] as const

/**
 * Shared hook for LinkedIn-account connection state.
 *
 * Used by:
 *   - Settings → LinkedIn Connect card (the source of truth UI)
 *   - The LinkedInRequiredGate that wraps Leads / Campaigns / Content / Inbox
 *     pages so a user without a connected account sees a "Connect first"
 *     screen instead of broken empty modules.
 *
 * Cached via React Query so the gate and the settings card share one fetch
 * and any connect/disconnect mutation invalidating linkedInStatusKey will
 * refresh both at once.
 */
export function useLinkedInStatus() {
  const apiFetch = useApi()

  return useQuery({
    queryKey: linkedInStatusKey,
    queryFn: async (): Promise<StatusResponse> => {
      const res = await apiFetch<StatusResponse>('/api/leads/linkedin/status')
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'LinkedIn status check failed')
      }
      return res.data
    },
    staleTime: 30_000,
  })
}
